import { useHubble } from '@seek/hubble';
import { useMelwaysInfo } from '@seek/melways-react';
import { MarketLocationIds, type Market } from '@ts-internal/shared-lib';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router';

import { useServerLogger } from 'src/App/providers/ServerLogProvider';
import { defaultPageSize } from 'src/App/services/search/searchDefaults';
import { useSearchQueryParams } from 'src/App/services/search/useSearchQueryParams';
import {
  usePublicProfileSearchQuery,
  type PublicProfileSearchQuery,
} from 'src/graphql/Queries/publicProfileSearch.generated';

export const useSearch = () => {
  const serverLogger = useServerLogger();
  const [isClient, setIsClient] = useState(false);
  const navigate = useNavigate();
  const { locale } = useMelwaysInfo();
  const {
    searchQueryFromUrl,
    marketFromUrl,
    pageNumberFromUrl,
    roleTitlesFromUrl,
    locationsFromUrl,
    companiesFromUrl,
    matchLatestCompanyFromUrl,
    matchLatestRoleTitleFromUrl,
  } = useSearchQueryParams();

  const visitorId = useHubble().visitorId();

  const [searchQueryState, setSearchQuery] = useState(searchQueryFromUrl);
  const [lastCommittedQuery, setLastCommittedQuery] =
    useState(searchQueryFromUrl);

  useEffect(() => {
    setIsClient(true);
  }, []);

  const { data, error, loading, refetch } = usePublicProfileSearchQuery({
    variables: {
      input: {
        searchQuery: searchQueryFromUrl,
        market: marketFromUrl,
        pageNumber: pageNumberFromUrl,
        pageSize: defaultPageSize,
        locations: {
          values: getLocationValues(locationsFromUrl, marketFromUrl),
        },
        roleTitles: {
          values: roleTitlesFromUrl,
          matchLatestOnly: matchLatestRoleTitleFromUrl,
        },
        companies: {
          values: companiesFromUrl,
          matchLatestOnly: matchLatestCompanyFromUrl,
        },
      },
      locale,
    },
    context: {
      headers: {
        'x-visitor-id': visitorId,
        'x-channel': 'web',
      },
    },
    notifyOnNetworkStatusChange: true,
    ssr: true,
    skip: !searchQueryFromUrl,
  });

  const searchResults: PublicProfileSearchQuery['publicProfileSearch'] =
    data?.publicProfileSearch;

  serverLogger?.debug(
    {
      activity: 'useSearch',
      searchResults,
    },
    'Search results fetched',
  );

  const csrSearch = useCallback(
    (
      {
        searchQuery,
        market,
        pageNumber,
        locations = [],
        roleTitles = [],
        matchLatestRoleTitle,
        companies = [],
        matchLatestCompany,
      }: {
        searchQuery: string;
        market: Market;
        pageNumber: number;
        locations?: string[];
        roleTitles?: string[];
        matchLatestRoleTitle: boolean;
        companies?: string[];
        matchLatestCompany: boolean;
      },
      {
        useLastCommittedQuery,
      }: {
        useLastCommittedQuery: boolean;
      },
    ) => {
      const queryToSend = determineQueryToSend({
        useLastCommittedQuery,
        searchQuery,
        lastCommittedQuery,
        setLastCommittedQuery,
        setSearchQuery,
      });

      navigate({
        search: new URLSearchParams({
          searchQuery: queryToSend,
          market,
          pageNumber: pageNumber.toString(),
          ...(roleTitles.length > 0
            ? { roleTitles: roleTitles.join(',') }
            : {}),
          ...(matchLatestRoleTitle ? { matchLatestRoleTitle: 'true' } : {}),
          ...(locations.length > 0 ? { locations: locations.join(',') } : {}),
          ...(companies.length > 0 ? { companies: companies.join(',') } : {}),
          ...(matchLatestCompany ? { matchLatestCompany: 'true' } : {}),
        }).toString(),
      });

      refetch({
        input: {
          searchQuery: queryToSend,
          market,
          pageNumber,
          pageSize: defaultPageSize,
          locations: { values: getLocationValues(locations, market) },
          roleTitles: {
            values: roleTitles,
            matchLatestOnly: matchLatestRoleTitle,
          },
          companies: {
            values: companies,
            matchLatestOnly: matchLatestCompany,
          },
        },
        locale,
      });
    },
    [navigate, refetch, locale, lastCommittedQuery],
  );

  return {
    searchResults,
    searchQueryState,
    setSearchQuery,
    csrError: isClient ? error : undefined,
    csrLoading: isClient ? loading : false,
    csrSearch,
  };
};

const getLocationValues = (
  locationNameWithIds: string[],
  currentMarket: Market,
): string[] => {
  const isLocationFilterSpecified = locationNameWithIds.length > 0;

  if (isLocationFilterSpecified) {
    const locationIds = locationNameWithIds
      .map((location) => location.split(' ').pop())
      .filter((id) => Boolean(id)) as string[];

    return locationIds;
  }

  return [MarketLocationIds[currentMarket]];
};

const determineQueryToSend = ({
  useLastCommittedQuery,
  searchQuery,
  lastCommittedQuery,
  setLastCommittedQuery,
  setSearchQuery,
}: {
  useLastCommittedQuery: boolean;
  searchQuery: string;
  lastCommittedQuery: string;
  setLastCommittedQuery: (query: string) => void;
  setSearchQuery: (query: string) => void;
}) => {
  if (useLastCommittedQuery) {
    setSearchQuery(lastCommittedQuery);
    return lastCommittedQuery;
  }

  setLastCommittedQuery(searchQuery);
  return searchQuery;
};
